<template>
  <div
    class="rs-select"
    :class="inputClasses"
  >
    <label v-if="label">{{ label }}<sup v-if="required">*</sup></label>
    <span v-if="helpTooltip">
      <RsIcon
        :id="`helperTooltip-${id}`"
        icon="question-circle"
        class="tooltip-question-icon"
      />
      <RsTooltip
        :target="`helperTooltip-${id}`"
        :title="helpTooltip"
        :position="helpTooltipPosition"
        :delay="{ show: 200 }"
      />
    </span>
    <Field
      v-slot="{ errors, meta }"
      class="veeValidate"
      as="div"
      :name="validationName"
      v-bind="validateOn"
      :rules="rules"
      :model-value="boundValue"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>
          This field is managed by <strong>[Data Source]</strong>.<br>Changing
          this will be temporary.
        </p>
      </div>
      <div class="input-group" role="group">
        <div
          v-if="imported"
          class="input-group-prepend"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'" />
          </span>
        </div>
        <div v-if="icon && !imported" class="input-group-prepend">
          <span class="input-group-icon">
            <RsIcon
              :icon="icon"
              weight="far"
            />
          </span>
        </div>
        <select
          :id="id"
          class="custom-select"
          :class="{
            'border-radius-right-curved': imported || icon,
            'border-radius-curved': !imported && !icon,
            'is-valid': getStateValidation(errors, meta.valid) === true,
            'is-invalid': getStateValidation(errors, meta.valid) === false,
          }"
          :disabled="disabled || (imported && isUnlocked === false)"
          @change.stop="onSelectChange"
        >
          <!-- When emptyOptionText is set, render a visible selectable "All" option
          (matches BFormSelect's clearable-empty-first-option behaviour from Vue 2).
          Otherwise fall back to the hidden placeholder so the select doesn't snap
          to its first real option when the value is empty. -->
          <option
            v-if="emptyOptionText"
            value=""
            :selected="!hasMatchedOption"
          >
            {{ emptyOptionText }}
          </option>
          <option
            v-else-if="!hasMatchedOption"
            value=""
            selected
            disabled
            hidden
          />
          <template v-for="opt in (selectOptions as any[])">
            <optgroup v-if="opt.options" :key="`group-${opt.label}`" :label="opt.label">
              <option
                v-for="sub in opt.options"
                :key="`${sub.value}`"
                :value="sub.value"
                :selected="isSelected(sub.value)"
                :disabled="sub.disabled"
              >
                {{ sub.text }}
              </option>
            </optgroup>
            <option
              v-else
              :key="`${opt.value}`"
              :value="opt.value"
              :selected="isSelected(opt.value)"
              :disabled="opt.disabled"
            >
              {{ opt.text }}
            </option>
          </template>
        </select>
      </div>
      <div
        v-if="errors.length && !meta.valid"
        class="text--error"
      >
        {{ errors[0] }}
      </div>
    </Field>
  </div>
</template>
<script lang="ts">
/* eslint-disable camelcase */
import { Field, defineRule } from 'vee-validate';
import { is_not, min, min_value, required } from '@vee-validate/rules';
import { defineComponent, PropType } from 'vue';
import type { TTooltipPosition } from '@/shared/types';

import i18n from '@/i18n';
import { TRsFormSelectOptions } from '@/shared/types/t-rs-form-select-options';

defineRule('min', min);
defineRule('min_value', min_value);
defineRule('required', (value: unknown) => (required(value) ? true : i18n.t('validation.fieldRequired') as string));
defineRule('is_not', is_not);

type TSelectValue = string | number | boolean | Record<string, unknown> | null | undefined;
type TEmptyValue = string | number | boolean | null | undefined;
type TSelectOption = { text: string; value: unknown; disabled: boolean };
type TSelectOptionGroup = { label: string; options: TSelectOption[] };

export default defineComponent({
  name: 'RsFormSelect',
  components: {
    Field,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    name: String,
    label: String,
    options: [Array, Object],
    required: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    filterName: String,
    value: [String, Number, Object, Boolean] as PropType<TSelectValue>,
    /**
     * Vue 3 v-model binding, kept alongside the legacy `value`/`input` pair so
     * both `v-model` and explicit `:value`+`@input` consumers work. `undefined`
     * means the parent did not bind v-model (see the `boundValue` computed).
     */
    modelValue: {
      type: [String, Number, Object, Boolean] as PropType<TSelectValue>,
      default: undefined,
    },
    /**
     *  Used to create a read-only input with the option to edit for imported fields.
    */
    imported: {
      type: Boolean,
      default: false,
    },
    emptyValue: {
      type: [String, Number, Boolean] as PropType<TEmptyValue>,
      default: undefined,
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String as PropType<TTooltipPosition>,
      default: 'right',
    },
    useNameForValidation: {
      type: Boolean,
    },
    icon: String,
    /**
     * Defines how form field validation is triggered within the `Field`.
     * Determines which user interactions (e.g., input, blur, or change events)
     * will cause the field to be validated.
     *
     * @see {@link https://vee-validate.logaretm.com/v2/guide/interaction.html }
     */
    validationMode: {
      type: String,
      default: 'aggressive',
      validator: (value: string) => ['aggressive', 'eager', 'passive', 'lazy'].includes(value),
    },
    /**
     * When true, displays the menu group that has the optional icon display on menu items
     */
    isOptionsWithIcons: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, displays the selected option's icon in the dropdown toggle button.
     */
    showSelectedIcon: {
      type: Boolean,
      default: false,
    },
    /**
     * When provided, renders a visible selectable first option with this text
     * (value `''`) so the user can always clear the selection. Mirrors
     * BFormSelect's clearable-first-option behaviour from Vue 2.
     */
    emptyOptionText: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'input'],
  data() {
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      dropdownOpen: false as boolean,
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): TSelectValue {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
    validationName(): string {
      if (this.useNameForValidation && this.name) {
        return this.name;
      }

      return this.label || 'field';
    },
    /**
     * Normalizes options to an array of { value, text, icon?, divider? } objects.
     */
    optionsArray(): Array<TRsFormSelectOptions> {
      if (!this.options || !Array.isArray(this.options)) {
        return [];
      }
      return this.options.map((opt: unknown) => {
        if (typeof opt === 'object' && opt !== null) {
          const o = opt as Record<string, unknown>;
          return {
            value: o.value,
            text: (o.text as string) || String(o.value),
            icon: o.icon as string | undefined,
            iconWeight: o.iconWeight as string | undefined,
            iconClass: o.iconClass as string | undefined,
            divider: o.divider as boolean | undefined,
          };
        }
        return { value: opt, text: String(opt) };
      });
    },
    /**
     * Returns the currently selected option object.
     */
    selectedOption(): TRsFormSelectOptions | undefined {
      return this.optionsArray.find((opt) => opt.value === this.value);
    },
    /**
     * Returns the text of the currently selected option for display in the dropdown toggle.
     */
    selectedOptionText(): string {
      return this.selectedOption ? this.selectedOption.text : '';
    },
    // vee-validate@4 dropped named interaction "modes" in favour of individual
    // validateOn* booleans on <Field> — approximate the old mode names here.
    validateOn(): Record<string, boolean> {
      switch (this.validationMode) {
        case 'passive':
          return {
            validateOnInput: false, validateOnChange: false, validateOnBlur: false, validateOnModelUpdate: false,
          };
        case 'lazy':
          return {
            validateOnInput: false, validateOnChange: true, validateOnBlur: false, validateOnModelUpdate: false,
          };
        case 'eager':
          return {
            validateOnInput: false, validateOnChange: true, validateOnBlur: true, validateOnModelUpdate: true,
          };
        case 'aggressive':
        default:
          return {
            validateOnInput: true, validateOnChange: true, validateOnBlur: true, validateOnModelUpdate: true,
          };
      }
    },
    selectOptions(): Array<TSelectOption | TSelectOptionGroup> {
      if (!this.options) return [];
      const opts: unknown[] = Array.isArray(this.options)
        ? this.options
        : Object.entries(this.options as Record<string, unknown>).map(([k, v]) => ({ value: k, text: String(v) }));
      return opts.map((opt: unknown) => {
        if (opt && typeof opt === 'object' && Array.isArray((opt as Record<string, unknown>).options)) {
          const group = opt as Record<string, unknown>;
          return {
            label: String(group.label ?? ''),
            options: (group.options as unknown[]).map((sub: unknown) => (typeof sub === 'object' && sub !== null
              ? { text: String((sub as Record<string, unknown>).text ?? (sub as Record<string, unknown>).value ?? ''), value: (sub as Record<string, unknown>).value, disabled: !!(sub as Record<string, unknown>).disabled }
              : { text: String(sub), value: sub, disabled: false })),
          };
        }
        if (opt && typeof opt === 'object') {
          const o = opt as Record<string, unknown>;
          return { text: String(o.text ?? o.value ?? ''), value: o.value, disabled: !!o.disabled };
        }
        return { text: String(opt ?? ''), value: opt, disabled: false };
      });
    },

    flatSelectOptions(): TSelectOption[] {
      return this.selectOptions.flatMap((opt) => ('options' in opt ? opt.options : [opt]));
    },
    hasMatchedOption(): boolean {
      return this.flatSelectOptions.some((opt) => this.isSelected(opt.value));
    },
  },
  methods: {
    /**
     * Sets the state to is-valid/is-invalid or none if not required.
     * @param errors Array of string errors
     * @param valid Boolean form validation
    */
    getStateValidation(errors: string[], valid: boolean): boolean | null {
      if (!this.required) {
        return null;
      }
      if (valid && !this.disabled) {
        return true;
      }
      if (errors.length) {
        return false;
      }
      return null;
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    isSelected(optValue: any): boolean {
      const current = this.getValueType(this.boundValue, this.emptyValue);
      if (current === optValue) return true;
      if (typeof current === 'object' && current !== null && typeof optValue === 'object' && optValue !== null) {
        return JSON.stringify(current) === JSON.stringify(optValue);
      }
      return String(current) === String(optValue);
    },
    onSelectChange(event: Event): void {
      const target = event.target as HTMLSelectElement;
      // emptyOptionText always renders as the first DOM option → always offset by 1.
      // Hidden placeholder only renders when nothing matches → offset by 1 in that case.
      let firstOptionOffset: number;
      if (this.emptyOptionText) {
        firstOptionOffset = 1;
      } else {
        firstOptionOffset = this.hasMatchedOption ? 0 : 1;
      }
      const optionIndex = target.selectedIndex - firstOptionOffset;
      const opt = this.flatSelectOptions[optionIndex];
      const out = opt !== undefined ? opt.value : target.value;
      this.$emit('update:modelValue', out);
      this.$emit('input', out);
    },
    getValueType(value: TSelectValue, emptyValue: TEmptyValue) {
      if (emptyValue === undefined && emptyValue !== null) {
        return (value || value === 0 || value === false ? value : '');
      }

      if (emptyValue !== value) {
        return value;
      }

      return emptyValue;
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

// Override Bootstrap is-valid style

.rs-select {
  position: relative;
  margin-bottom: 1rem;

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
    font-size: $form-label-font-size;
    font-weight: $form-label-font-weight;
  }
  select {
    width: 100%;
    height: 40px;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    font-size: $input-font-size;
    color: $input-color;
    padding: 0.5rem 1.5rem 0.5rem 0.5rem;
  }
  .custom-select {
    background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='gray' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E");
    &.is-valid {
      // Override bootstrap style
      background: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='gray' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") right .75rem center/8px 10px no-repeat;
      border-color: #c9ccdf;
      &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
      }
    }
  }
  .custom-select:disabled {
    color: #0f0f0f;
    opacity: 1;
  }
  .border-radius-right-curved {
    border-radius: 0 6px 6px 0!important;
  }
  .border-radius-curved {
    border-radius: $input-radius!important;
  }
  option {
    background-color: $white;
    box-sizing: border-box;
    &:focus {
      border-color: $info;
    }
  }
  .input-group-icon {
    padding: 0.375rem 0.65rem;
    color: rgba($tertiary-muted, 0.4);
    background-color: $white;
    border: solid 1px #C9CCDF;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .rs-select-dropdown {
    width: 100%;

    .btn {
      width: 100%;
      height: calc(1.5em + 0.75rem + 2px);
      padding: 0.375rem 0.75rem;
      text-align: left;
      background-color: $white;
      border: 1px solid #c9ccdf;
      border-radius: $input-radius;
      font-size: $input-font-size;
      font-weight: 400;
      line-height: 1.5;
      color: $input-color;
      display: flex;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='gray' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.75rem center;
      background-size: 8px 10px;
      padding-right: 1.75rem;

      &:hover {
        background-color: $white;
        border-color: #c9ccdf;
      }

      &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
      }

      &:disabled {
        color: #0f0f0f;
        background-color: #e9ecef;
        opacity: 1;
      }

      &::after {
        display: none;
      }
    }

    .dropdown-menu {
      border: 1px solid #c9ccdf;
      border-radius: $input-radius;
      padding: 0.25rem 0;
      margin-top: 2px;
      min-width: 100%;
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      padding: 0.375rem 0.75rem;
      font-size: $input-font-size;
      color: $input-color;

      &:hover {
        background-color: #f8f9fa;
      }

      &.active,
      &:active {
        background-color: $info;
        color: $white;
      }
    }

    .dropdown-divider {
      margin: 0.25rem 0;
    }

    .rs-select-btn-content {
      min-width: 0;
      overflow: hidden;
    }

    .rs-select-btn-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
